import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthOnlyRoute - Requires authentication but NOT subscription
 * Used for pages like billing success that need to load before subscription is active
 */
export default function AuthOnlyRoute({ children }) {
  const [status, setStatus] = useState('loading');
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        // Check Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          if (mounted) {
            setStatus('unauthenticated');
          }
          return;
        }

        // Sync with backend to ensure session cookie is set
        try {
          await fetch(`${BACKEND_URL}/api/auth/supabase-sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              supabase_user_id: session.user.id,
              email: session.user.email,
              full_name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0],
            }),
          });
        } catch (syncError) {
          console.error('Backend sync error:', syncError);
        }

        // User is authenticated - allow access
        if (mounted) {
          setStatus('authenticated');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        if (mounted) {
          setStatus('unauthenticated');
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setStatus('unauthenticated');
      } else if (event === 'SIGNED_IN') {
        checkAuth();
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFD]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9E00] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    localStorage.setItem('redirectAfterAuth', location.pathname + location.search);
    return <Navigate to="/auth" replace />;
  }

  return children;
}
